import LineButton from 'components/button/LineButton';
import config from 'config/appConfig';
import { DivConst } from 'constants/divConst';
import GoogleAnalyticsHelper from 'helpers/GoogleAnalyticsHelper';
import { AppHelper } from 'helpers/appHelper';
import { useApiState } from 'hooks/useApiState';
import usePageTracking from 'hooks/usePageTracking';
import Error404 from 'pages/error/Error404';
import { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { QuizContentState } from 'stores/atoms/quizContentState';
import { QuizRespondentState } from 'stores/atoms/quizRespondentState';
import { QuizPublishType } from 'types/quizPublishType';

/**
 * トップ（LINE登録）画面
 */
export default function QuizLineTop(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { quizCd } = useParams();
  const [quizContentState, setContentState] = useRecoilState(QuizContentState);
  const [quizRespondentState, setQuizRespondentState] = useRecoilState(QuizRespondentState);

  const { postQuizDisplay } = useApiState();

  const [initialized, setInitialized] = useState(false);

  const [notFoundState, setNotFoundState] = useState(false);
  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    _initState();
  }, []);

  useEffect(() => {
    if (quizContentState?.quiz?.gtmId && !quizRespondentState?.preview) {
      TagManager.initialize({ gtmId: quizContentState.quiz.gtmId });
    }
  }, [quizContentState, quizRespondentState]);

  // GTMトラッキング
  usePageTracking(quizContentState?.quiz?.gtmId, location.pathname, quizRespondentState?.preview);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  const _initState = async () => {
    try {
      // URLの取得
      const url = `${config.s3.bucket.staticContents.endpoint}/quiz/${'line'}/${quizCd}.json`;

      // クイズデータの取得
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-cache',
      });
      const json: QuizPublishType = await response.json();
      _patchJson(json);
      setInitialized(true);

      // bodyのクラスを設定
      document.body.classList.add(AppHelper.getDesignClassName(json.quiz.quizTemplateId));

      // クイズ表示APIの呼び出し
      postQuizDisplay({
        quizId: json.quiz.id,
      });

      // クイズ回答者ステートの初期化
      setQuizRespondentState({
        id: null,
        page: 'top',
        answers: [],
        preview: false,
        respondentForm: null,
      });

      // GA4に送信
      GoogleAnalyticsHelper.initialize([config.ga4.trackingId, json.company.apiKeyGoogleAnalytics]);
      GoogleAnalyticsHelper.pageview(`/quiz/line/${quizCd}`, json.quiz.title);
    } catch (e) {
      setNotFoundState(true);
    }
  };

  /* JSON定義のパッチ処理（バージョンまたぎJSON対応） */
  const _patchJson = async (json: QuizPublishType) => {
    const patchedJson = json;
    if (!json.quiz.respondentFields) {
      // 取得フィールドがない状態（項目追加対応前の場合）
      // メールアドレスのフィールドを追加
      patchedJson.quiz.respondentFields = [
        {
          getFlag: json.quiz.respondentMailDiv !== DivConst.items.quizRespondentMail.none.value,
          id: 0,
          label: null,
          maxLength: null,
          no: 1,
          placeholder: null,
          respondentCustomFieldDiv: null,
          respondentCustomFieldInputDiv: null,
          respondentFieldDiv: 10,
          respondentStandardFieldDiv: 10,
        },
      ];
    }
    setContentState(patchedJson);
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {notFoundState && <Error404 />}

      {initialized && !notFoundState && (
        <>
          <main>
            <section className="section-common-quiz">
              <div className="container">
                <div className="card card-quiz">
                  <div className="card-body">
                    {/* クイズのタイトル */}
                    {!quizContentState.quiz.titleHiddenFlag && (
                      <h1 className="title-section">{quizContentState.quiz.title}</h1>
                    )}
                    {/* 表紙の画像 */}
                    {quizContentState.quiz?.imageKey && (
                      <img
                        src={AppHelper.getImageUrl(quizContentState.quiz.imageKey)}
                        className="img-fluid d-block mx-auto img-lg"
                      />
                    )}
                    {/* 説明文 */}
                    <pre>{quizContentState.quiz.description}</pre>
                    {/* LINE友だち追加ボタン */}
                    <LineButton url={quizContentState.quiz.lineUrl} />
                    {/* 利用規約、プライバシーポリシー */}
                    <p className="text-center">
                      LINEを友だち追加することよって開始することができます。
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </main>
        </>
      )}
    </>
  );
}
